import { useEffect, useCallback } from 'react';
import { useNavigate, generatePath, useParams } from 'react-router-dom';
import { ValidatorsProps } from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { changePanelState } from '../../store/slices/groups';
import { useCreateGroupMutation } from '../../store/services/groups';
import useChartForm from '../../hooks/use-form';

import ROUTES from '../../../../routes';

const useCreate = () => {
  const {
    accounts: { typeOptions },
    categories: { categories },
    groups: { groups, panelIsOpen, groupsValidation },
  } = useAppSelector((state) => state.charts);
  const { activePermissions } = useAppSelector((state) => state.root);

  const dispatch = useAppDispatch();

  const { categoryId, primaryAccountId, groupId } = useParams();
  const [create, { isLoading: requestInProgress, isSuccess: onChangeSuccess }] =
    useCreateGroupMutation();

  const { description, group, control, handleSubmit, errors, reset } =
    useChartForm({
      groups,
      categories,
      panelIsOpen,
      schema: groupsValidation as ValidatorsProps,
    });

  const navigate = useNavigate();

  const handleClose = useCallback(() => {
    dispatch(changePanelState(false));

    setTimeout(() => {
      navigate(
        generatePath(ROUTES.PRIMARY_ACCOUNTS, {
          categoryId: categoryId as string,
          groupId: groupId as string,
          primaryAccountId: primaryAccountId as string,
        }),
      );
    }, 100);
  }, [dispatch]);

  const onSubmit = handleSubmit((values) => {
    const body: {
      description: string;
      categoryId: string;
    } = {
      description: values.description as string,
      categoryId: values.categoryId as string,
    };

    create(body);
  });

  useEffect(() => {
    if (categoryId) {
      reset({ categoryId });
    }
  }, [categoryId, reset]);

  useEffect(() => {
    dispatch(changePanelState(true));

    return () => {
      dispatch(changePanelState(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (onChangeSuccess) {
      handleClose();
    }
  }, [onChangeSuccess, handleClose]);

  return {
    typeOptions,
    activePermissions,
    requestInProgress,
    description,
    group,
    control,
    errors,
    onSubmit,
    categories,
    groups,
    panelIsOpen,
    handleClose,
    categoryId,
  };
};

export default useCreate;
